import { add, listen, qs, qsa, remove, toggle } from '@fluorescent/dom';
import isMobile from 'is-mobile';
import { cart, getProduct, section } from '@/glow';
import { emit, on } from '@/glow/events';
import { slideDown, slideStop, slideUp } from 'slide-anim';

import { getVariantFromId } from '@shopify/theme-product';
import Lightbox from '@/lib/lightbox';
import Media from '@/lib/media';
import ProductForm from '@/lib/product-form';
import { switchImage } from '@/utils';
import {
  quantityInput,
  variantPopup,
  updatePrices,
  updateSku,
  updateBuyButton,
  optionButtons,
  inventoryCounter,
} from '@/lib/product';
import { updateUnitPrices } from '@/lib/unit-pricing';
import storeAvailability from '@/lib/store-availability';
import accordions from '@/lib/accordions';
import dispatchCustomEvent from '@/lib/dispatch-custom-event';

const selectors = {
  form: '[data-product-form]',
  addToCart: '[data-add-to-cart]',
  variantSelect: '[data-variant-select]',
  optionById: id => `[value='${id}']`,
  thumb: '[data-product-thumbnail]',
  shippingEstimatorButton: '[data-estimator-trigger]',
  storeAvailability: '[data-store-availability-container]',
  quantityError: '[data-quantity-error]',
};

section('featured-product', {
  productForm: null,
  events: [],
  accordions: [],

  onLoad() {
    const { placeholder, productHasOnlyDefaultVariant } =
      this.container.dataset;

    if (placeholder === 'true') return;

    this.formElement = qs(selectors.form, this.container);

    this.images = qsa('[data-open]', this.container);
    this.quantityError = qs(selectors.quantityError, this.container);

    const viewInYourSpace = qs('[data-in-your-space]', this.container);
    viewInYourSpace && toggle(viewInYourSpace, 'visible', isMobile());

    // Handle Surface pickup
    this.storeAvailabilityContainer = qs(
      selectors.storeAvailability,
      this.container
    );
    this.availability = null;

    if (this.formElement) {
      const { productHandle, currentProductId } = this.formElement.dataset;
      const product = getProduct(productHandle);
      product(data => {
        const variant = getVariantFromId(data, parseInt(currentProductId));

        if (this.storeAvailabilityContainer && variant) {
          this.availability = storeAvailability(
            this.storeAvailabilityContainer,
            data,
            variant,
            { hideVariantTitle: productHasOnlyDefaultVariant === 'true' }
          );
        }

        this.productForm = ProductForm(this.container, this.formElement, data, {
          onOptionChange: e => this.onOptionChange(e),
          onFormSubmit: e => this.onFormSubmit(e),
          onQuantityChange: e => this.onQuantityChange(e),
        });

        const productInventoryJson = qs(
          '[data-product-inventory-json]',
          this.container
        );

        if (productInventoryJson) {
          const jsonData = JSON.parse(productInventoryJson.innerHTML);
          const variantsInventories = jsonData.inventory;

          if (variantsInventories) {
            const config = {
              id: variant.id,
              variantsInventories,
            };
            this.inventoryCounter = inventoryCounter(this.container, config);
          }
        }
      });
    }

    const accordionElements = qsa('.accordion', this.container);
    accordionElements.forEach(accordion => {
      const accordionOpen = accordion.classList.contains('accordion--open');
      this.accordions.push(accordions(accordion, { firstOpen: accordionOpen }));

      const accordionParent = accordion.parentElement;
      if (
        accordionParent.classList.contains('rte--product') &&
        !accordionParent.classList.contains('accordion accordion--product')
      ) {
        accordion.classList.add('rte--product', 'accordion--product');
      }
    });

    this.lightbox = Lightbox(qs('[data-lightbox]', this.container));
    this.media = Media(
      qs('.featured-product__media-container', this.container)
    );
    this.optionButtons = optionButtons(
      qsa('[data-option-buttons]', this.container)
    );
    this.quantityInput = quantityInput(this.container);
    this.variantPopup = variantPopup(this.container);
    this.socialButtons = qsa('[data-social-share]', this.container);

    this._initEvents();
  },

  _initEvents() {
    this.events.push(
      listen(this.images, 'click', e => {
        e.preventDefault();

        this.lightbox.open(e.currentTarget.dataset.open);
      })
    );
    this.events.push(
      listen(this.socialButtons, 'click', e => {
        toggle(e.target, 'active');
        const sub = qs('.article__share-icons', e.target);

        sub.setAttribute('aria-hidden', !contains(e.target, 'active'));
      })
    );
  },

  // When the user changes a product option
  onOptionChange({ dataset: { variant } }) {
    const buyButton = qs(selectors.addToCart, this.container);

    // Update prices to reflect selected variant
    updatePrices(this.container, variant);

    // Update buy button
    updateBuyButton(buyButton, variant);

    // Update unit pricing
    updateUnitPrices(this.container, variant);

    // Update sku
    updateSku(this.container, variant);

    dispatchCustomEvent('product:variant-change', { variant: variant });

    this.inventoryCounter && this.inventoryCounter.update(variant);

    if (!variant) {
      updateBuyButton(qs('[data-add-to-cart]', this.container), false);

      this.availability && this.availability.unload();
      return;
    }

    // We need to set the id input manually so the Dynamic Checkout Button works
    const selectedVariantOpt = qs(
      `${selectors.variantSelect} ${selectors.optionById(variant.id)}`,
      this.container
    );
    selectedVariantOpt.selected = true;

    // We need to dispatch an event so Shopify pay knows the form has changed
    this.formElement.dispatchEvent(new Event('change'));

    // Move screen or mobile slider to selected variants media
    if (variant.featured_media) {
      switchImage(this.container, variant.featured_media.id);
    }

    // Update product availability content
    this.availability && this.availability.update(variant);
  },

  // When user submits the product form
  onFormSubmit(e) {
    const { enableCartAjax } = document.body.dataset;

    // If a featured product section is on the cart page we will need to refresh
    // the cart to show a product has been added.
    const cartPage = document.body.classList.contains('template-cart');

    if (!enableCartAjax || cartPage) return;

    e.preventDefault();

    add(this.quantityError, 'hidden');

    const button = qs(selectors.addToCart, this.container);
    add(button, 'loading');
    cart
      .addItem(this.formElement)
      .then(({ item }) => {
        remove(button, 'loading');
        emit('cart:open', null, { flash: item.variant_id });
        dispatchCustomEvent('cart:item-added', { product: item });
      })
      .catch(() => {
        remove(this.quantityError, 'hidden');
        const button = qs(selectors.addToCart, this.container);
        remove(button, 'loading');
      });
  },

  // When user updates quantity
  onQuantityChange({ dataset: { variant, quantity } }) {
    // Adjust the hidden quantity input within the form
    const quantityInputs = [...qsa('[name="quantity"]', this.formElement)];
    quantityInputs.forEach(quantityInput => {
      quantityInput.value = quantity;
    });

    dispatchCustomEvent('product:quantity-update', {
      quantity: quantity,
      variant: variant,
    });
  },

  onBlockSelect({ target }) {
    const label = qs('.accordion__label', target);
    target.scrollIntoView({ block: 'center', behavior: 'smooth' });

    if (!label) return;
    const { parentNode: group, nextElementSibling: content } = label;

    slideStop(content);
    slideDown(content);
    group.setAttribute('data-open', true);
    label.setAttribute('aria-expanded', true);
    content.setAttribute('aria-hidden', false);
  },

  onBlockDeselect({ target }) {
    const label = qs('.accordion__label', target);
    if (!label) return;
    const { parentNode: group, nextElementSibling: content } = label;

    slideStop(content);
    slideUp(content);
    group.setAttribute('data-open', false);
    label.setAttribute('aria-expanded', false);
    content.setAttribute('aria-hidden', true);
  },

  onUnload() {
    this.productForm.destroy();
    this.lightbox.destroy();
    this.optionButtons.destroy();
    this.quantityInput.unload();
    this.events.forEach(unsubscribe => unsubscribe());
    this.accordions.forEach(accordion => accordion.unload());
  },
});
